import React, { useState } from 'react';

import { sortBy } from 'lodash';

import useUserHook from 'hooks/useUserHook';

import { useConfigurationStore } from 'store/configurationsStore';

import { useQueryAssigneeList } from 'api/queries/assigneeList.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import RichTextArea from 'components/common/richTextArea/richTextArea';

import { NarrativeCustomButtons } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/EditNarrative/NarrativeCustomButtons/narrativeCustomButtons';
import 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/EditNarrative/editNarrative.css';
import classes from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/Narrative/narrative.module.scss';
import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { Button } from 'components/DesignSystem/Button';
import { CustomizedTooltip } from 'components/common';

export const userMentionMapper = (i) => ({
    text: `${i.firstName} ${i.lastName}`,
    value: `${i.firstName} ${i.lastName}`,
    url: i.userName,
});

interface Props {
    handleCancel: () => void;
    onSave: (description: string, isGenerativeAi: boolean) => void;
    value: string;
}

export const EditNarrative = ({ value, handleCancel, onSave }: Props) => {
    const configuration = useConfigurationStore();
    const { ability } = useUserHook();
    const isAdmin = ability.can('all', 'Admin');
    const { isGenerativeAi, setIsGenerativeAi, componentKey, isLoading } = usePinboardWidgetWrapperContext();
    const [description, setDescription] = useState<string>('');
    const onEditorDescription = (value: string) => {
        setDescription(value);
    };
    const { data: assigneeList } = useQueryAssigneeList();
    const widgetConfigInReport = componentKey ? configuration[componentKey as keyof IHashmap] : undefined;
    const shouldShowGenerateAiNarrativeToggleMenu = widgetConfigInReport?.componentMenu?.useAutomatedNarrative;

    return (
        <div className={classes.narrativeContainer}>
            <div className={`narrative-text-editor ${isGenerativeAi ? 'disabled' : ''}`}>
                <CustomizedTooltip
                    tooltipPosition={TooltipPosition.Right}
                    tooltipContent={'Turn off automated narrative to write your own.'}
                    disabled={!isGenerativeAi}
                    triggerElement={
                        <RichTextArea
                            onEditorDescription={onEditorDescription}
                            existingData={value}
                            editorContentHeight={180}
                            isDisable={isGenerativeAi}
                            isAutoFocus={true}
                            isLoading={isLoading}
                            loadingText={'Generating narrative...'}
                            isClearButton
                            customToolbarButtons={[
                                shouldShowGenerateAiNarrativeToggleMenu && (
                                    <NarrativeCustomButtons
                                        key={'generative-ai-btn'}
                                        isAdmin={isAdmin}
                                        componentKey={componentKey}
                                        isGenerativeAi={isGenerativeAi}
                                        setIsGenerativeAi={setIsGenerativeAi}
                                    />
                                ),
                            ].filter(Boolean)}
                            mention={{
                                separator: ' ',
                                trigger: '@',
                                suggestions:
                                    sortBy(
                                        assigneeList?.map(userMentionMapper),
                                        [(o) => o.text.toLowerCase()],
                                        ['asc'],
                                    ) || [],
                            }}
                        />
                    }
                />
            </div>

            <div className={classes.footer}>
                <div className={classes.buttonsWrapper}>
                    <Button size={'large'} variant={'secondary'} onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        size={'large'}
                        variant={'primary'}
                        onClick={() => onSave(description, isGenerativeAi ?? false)}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};
